import type { ComplaintHistoryEvent, ComplaintStatus, MediaWithUrl } from '../../api';
import { useState } from 'react';
import {
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  MessageSquare,
  Image,
  User,
  FileText,
  ExternalLink,
  Pencil,
  ChevronDown,
} from 'lucide-react';

interface HistoryTimelineProps {
  history: ComplaintHistoryEvent[];
}

const statusConfig: Record<ComplaintStatus, { label: string; color: string; bgColor: string }> = {
  submitted: {
    label: 'Submitted',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
  },
  under_review: {
    label: 'Under Review',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
  },
  approved: {
    label: 'Approved',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
  rejected: {
    label: 'Rejected',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
  },
};

function isImageFile(key: string): boolean {
  return /\.(jpg|jpeg|png|gif|webp)$/i.test(key);
}

function isPdfFile(key: string): boolean {
  return /\.pdf$/i.test(key);
}

function getFileName(key: string): string {
  const parts = key.split('/');
  return parts[parts.length - 1];
}

const FIELD_LABELS: Record<string, string> = {
  manufacturer: 'Manufacturer',
  deliveryDate: 'Delivery Date',
  batchCode: 'Batch Code',
  affectedUnits: 'Affected Units',
  claimValue: 'Claim Value',
  description: 'Description',
  productName: 'Product Name',
  category: 'Category',
};

function getFieldLabel(field: string): string {
  return FIELD_LABELS[field] || field.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
}

function getEventIcon(event: ComplaintHistoryEvent) {
  if (event.eventType === 'field_edited') {
    return <Pencil className="w-4 h-4" />;
  }
  if (event.eventType === 'comment') {
    // Show image icon if comment has media attachments
    if ((event.media && event.media.length > 0) || (event.mediaUrls && event.mediaUrls.length > 0)) {
      return <Image className="w-4 h-4" />;
    }
    return <MessageSquare className="w-4 h-4" />;
  }
  if (event.eventType === 'media_added') {
    return <Image className="w-4 h-4" />;
  }
  // status_change
  if (event.newStatus === 'approved') {
    return <CheckCircle2 className="w-4 h-4" />;
  }
  if (event.newStatus === 'rejected') {
    return <XCircle className="w-4 h-4" />;
  }
  if (event.newStatus === 'under_review') {
    return <AlertCircle className="w-4 h-4" />;
  }
  return <Clock className="w-4 h-4" />;
}

function getEventColor(event: ComplaintHistoryEvent) {
  if (event.eventType === 'field_edited') {
    return 'bg-blue-100 text-blue-600';
  }
  if (event.eventType === 'comment') {
    return 'bg-gray-100 text-gray-600';
  }
  if (event.eventType === 'media_added') {
    return 'bg-purple-100 text-purple-600';
  }
  // status_change
  if (event.newStatus) {
    return `${statusConfig[event.newStatus].bgColor} ${statusConfig[event.newStatus].color}`;
  }
  return 'bg-gray-100 text-gray-600';
}

function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getEventTitle(event: ComplaintHistoryEvent): string {
  if (event.eventType === 'field_edited') {
    return `Fields edited by ${event.userName}`;
  }
  if (event.eventType === 'comment') {
    return 'Comment added';
  }
  if (event.eventType === 'media_added') {
    return 'Media added';
  }
  // status_change
  if (event.newStatus) {
    const config = statusConfig[event.newStatus];
    if (event.previousStatus) {
      return `Status changed to ${config.label}`;
    }
    return `Complaint ${config.label}`;
  }
  return 'Event';
}

// Fields that are derived snapshots of another canonical field and should not
// appear as their own row in the activity log (HBAC2-8835). Filtered here as
// well so historical events that were stored before the backend fix still
// render cleanly.
const HIDDEN_HISTORY_FIELDS = new Set<string>(['category']);

function FieldChangesDetail({
  fieldChanges,
}: {
  fieldChanges: Array<{ field: string; oldValue: string; newValue: string }>;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const visibleChanges = (fieldChanges ?? []).filter(
    (change) => !HIDDEN_HISTORY_FIELDS.has(change.field)
  );

  if (visibleChanges.length === 0) return null;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
      >
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-0' : '-rotate-90'}`}
        />
        {visibleChanges.length} field{visibleChanges.length !== 1 ? 's' : ''} changed
      </button>
      {isOpen && (
        <div className="mt-1.5 space-y-1 pl-5">
          {visibleChanges.map((change) => (
            <div key={change.field} className="text-xs text-gray-600">
              <span className="font-medium text-gray-700">{getFieldLabel(change.field)}:</span>{' '}
              <span className="text-red-500 line-through">{change.oldValue || '(empty)'}</span>{' '}
              <span className="text-gray-400">&rarr;</span>{' '}
              <span className="text-green-600">{change.newValue || '(empty)'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function HistoryTimeline({ history }: HistoryTimelineProps) {
  if (history.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Clock className="w-8 h-8 mx-auto mb-2 text-gray-400" />
        <p>No history yet</p>
      </div>
    );
  }

  // Sort by timestamp descending (most recent first)
  const sortedHistory = [...history].sort((a, b) => b.timestamp - a.timestamp);

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {sortedHistory.map((event, index) => (
          <li key={`${event.timestamp}-${index}`}>
            <div className="relative pb-8">
              {/* Connecting line */}
              {index !== sortedHistory.length - 1 && (
                <span
                  className="absolute left-4 top-8 -ml-px h-full w-0.5 bg-gray-200"
                  aria-hidden="true"
                />
              )}

              <div className="relative flex items-start space-x-3">
                {/* Icon */}
                <div
                  className={`relative flex h-8 w-8 items-center justify-center rounded-full ring-4 ring-white ${getEventColor(event)}`}
                >
                  {getEventIcon(event)}
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm font-medium text-gray-900">{getEventTitle(event)}</p>
                    <time className="text-xs text-gray-500 whitespace-nowrap">
                      {formatTimestamp(event.timestamp)}
                    </time>
                  </div>

                  {/* User info */}
                  <div className="mt-1 flex items-center gap-1.5 text-sm text-gray-500">
                    <User className="w-3.5 h-3.5" />
                    <span>{event.userName}</span>
                    <span className="text-gray-300">|</span>
                    <span className="capitalize">{event.userType}</span>
                  </div>

                  {/* Comment text */}
                  {event.comment && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                      {event.comment}
                    </div>
                  )}

                  {/* Field changes for field_edited events */}
                  {event.eventType === 'field_edited' && event.fieldChanges && (
                    <FieldChangesDetail fieldChanges={event.fieldChanges} />
                  )}

                  {/* Media attachments on comments */}
                  {event.media && event.media.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {event.media.map((media: MediaWithUrl) => (
                        <a
                          key={media.key}
                          href={media.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group relative block"
                        >
                          {isImageFile(media.key) ? (
                            <div className="w-20 h-20 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden hover:border-gray-400 transition-colors">
                              <img
                                src={media.downloadUrl}
                                alt="Attachment"
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                <ExternalLink className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </div>
                          ) : isPdfFile(media.key) ? (
                            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg border border-red-200 hover:border-red-400 transition-colors">
                              <FileText className="w-5 h-5 text-red-600 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-gray-900 truncate max-w-[120px]">
                                  {getFileName(media.key)}
                                </p>
                                <p className="text-[10px] text-gray-500">PDF</p>
                              </div>
                              <ExternalLink className="w-3 h-3 text-gray-400 group-hover:text-red-600 transition-colors flex-shrink-0" />
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-400 transition-colors">
                              <Image className="w-5 h-5 text-gray-500 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-gray-900 truncate max-w-[120px]">
                                  {getFileName(media.key)}
                                </p>
                              </div>
                              <ExternalLink className="w-3 h-3 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" />
                            </div>
                          )}
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Status badges for status change */}
                  {event.eventType === 'status_change' && event.previousStatus && event.newStatus && (
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs ${statusConfig[event.previousStatus].bgColor} ${statusConfig[event.previousStatus].color}`}
                      >
                        {statusConfig[event.previousStatus].label}
                      </span>
                      <span className="text-gray-400">&rarr;</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs ${statusConfig[event.newStatus].bgColor} ${statusConfig[event.newStatus].color}`}
                      >
                        {statusConfig[event.newStatus].label}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
